import PropTypes from "prop-types";
import { faExclamationTriangle, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Component } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { FormattedDate, FormattedMessage, injectIntl } from "react-intl";
import { groupActivationsByVersionAndYear } from "../../../utils/TemplateActivationUtils";
import SurveyUtils from "../../../utils/SurveyUtils";
import Table from "react-bootstrap/Table";

class ActivationInfoDialog extends Component {
  render() {
    const { show, template, closeDialog, activationHistory, intl } = this.props;
    const rows = groupActivationsByVersionAndYear(activationHistory ?? []);
    const isSemesterTypeProgram = SurveyUtils.isSemesterTypeProgram(template.program?.name);
    const columns = [
      {
        name: "version",
        labelKey: "templateList.version",
        customBodyRenderer: (row) => {
          const updatedLabel =
            row.updated == null
              ? null
              : intl.formatMessage(
                  { id: "templateList.activations.versionUpdated" },
                  {
                    date: intl.formatDate(new Date(row.updated), {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit"
                    })
                  }
                );
          return (
            <span className="text-nowrap">
              {row.version}
              {updatedLabel && (
                <OverlayTrigger placement="top" overlay={<Tooltip>{updatedLabel}</Tooltip>}>
                  <span className="ms-1 text-muted" tabIndex={0} role="img" aria-label={updatedLabel}>
                    <FontAwesomeIcon icon={faInfoCircle} size="sm" />
                  </span>
                </OverlayTrigger>
              )}
            </span>
          );
        }
      },
      {
        name: "year",
        labelKey: isSemesterTypeProgram
          ? "respondents.overview.reportPeriod"
          : "templateList.activations.reportingYear",
        customBodyRenderer: (row) =>
          row.year == null ? "" : SurveyUtils.getReportPeriodLabel(intl, template.program?.name, row.year)
      },
      {
        name: "activated",
        labelKey: "templateList.activations.activation",
        customBodyRenderer: (result) => {
          return result.activated ? (
            <>
              <FormattedDate
                value={new Date(result.activated)}
                year="numeric"
                month="2-digit"
                day="2-digit"
                hour="numeric"
                minute="2-digit"
                second="2-digit"
              />
              {result.isVersionActivationFallback && (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      <FormattedMessage id="templateList.activations.lastVersionActivation" />
                    </Tooltip>
                  }
                >
                  <span
                    className="ms-1 text-warning"
                    tabIndex={0}
                    role="img"
                    aria-label={intl.formatMessage({
                      id: "templateList.activations.lastVersionActivation"
                    })}
                  >
                    <FontAwesomeIcon icon={faExclamationTriangle} size="sm" />
                  </span>
                </OverlayTrigger>
              )}
            </>
          ) : (
            ""
          );
        }
      },
      {
        name: "title",
        labelKey: "templateList.activations.activation.title"
      },
      {
        name: "targetName",
        labelKey: "templateList.activations.program.instance"
      },
      {
        name: "targetForeignId",
        labelKey: "templateList.activations.program.instance.foreignid"
      },
      {
        name: "numberOfPublished",
        labelKey: isSemesterTypeProgram
          ? "templateList.activations.semester.program.amount"
          : "templateList.activations.amount"
      }
    ];
    return (
      <Modal size="xl" show={show} className="read-only">
        <Modal.Header>
          <Modal.Title>
            <FormattedMessage id="templateList.activations.previous" />
            &nbsp;
            <FormattedMessage
              id={
                isSemesterTypeProgram
                  ? "templateList.activations.preactivations"
                  : "templateList.activations.activations"
              }
            />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h6>
            <FormattedMessage id="templateList.activations.template.title" />
          </h6>
          <p>{template.surveyName}</p>

          <Table borderless>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.name} scope="col">
                    <FormattedMessage id={column.labelKey} />
                  </th>
                ))}
              </tr>
            </thead>
            {rows.map((group, groupIndex) => (
              <tbody
                key={JSON.stringify([group.version, group.year])}
                className={groupIndex % 2 === 0 ? "table-light" : undefined}
              >
                {group.activations.map((activation, index) => (
                  <tr key={index}>
                    {columns.map((column, columnIndex) => {
                      const isGroupColumn = columnIndex < 2;
                      if (isGroupColumn && index > 0) return null;
                      const item = isGroupColumn ? group : activation;
                      return (
                        <td key={column.name} rowSpan={isGroupColumn ? group.activations.length : undefined}>
                          {column.customBodyRenderer ? column.customBodyRenderer(item) : item[column.name]}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            ))}
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={closeDialog} block>
            <FormattedMessage id="app.common.Close" />
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

ActivationInfoDialog.propTypes = {
  show: PropTypes.bool
};

export default injectIntl(ActivationInfoDialog);
